import moment, {type MomentInput} from 'moment'

export type VosDisputeFieldKey =
  | 'startTime'
  | 'endTime'
  | 'duration'
  | 'breakTime'
  | 'mileage'
  | 'roundTrip'
  | 'travelTime'

export type VosDisputeFieldDiffs = Record<VosDisputeFieldKey, boolean>

export const VOS_DISPUTE_ROW_HIGHLIGHT_CLASS = 'd-flex mb-2 rounded px-2 py-1 bg-light-danger'
export const VOS_DISPUTE_ROW_CLASS = 'd-flex mb-2'

function sameInstant(a: unknown, b: unknown): boolean {
  if (a == null && b == null) return true
  if (a == null || b == null) return false
  const ma = moment(a as MomentInput)
  const mb = moment(b as MomentInput)
  if (!ma.isValid() || !mb.isValid()) return String(a) === String(b)
  return ma.valueOf() === mb.valueOf()
}

function sameNumber(a: unknown, b: unknown): boolean {
  const na = a == null || a === '' ? null : Number(a)
  const nb = b == null || b === '' ? null : Number(b)
  if (na == null && nb == null) return true
  if (na == null || nb == null) return false
  return na === nb
}

export function isVosDisputeStatus(
  meetingSummary: {status?: number} | null | undefined,
  options: {
    isDigitalVos: boolean
    requesterVosDisputed: boolean
    hasRequesterVOSSubmission: boolean
  }
): boolean {
  if (!meetingSummary) return false
  if (meetingSummary.status === 4) return true
  return (
    options.isDigitalVos &&
    options.requesterVosDisputed &&
    options.hasRequesterVOSSubmission
  )
}

export function getVosDisputeFieldDiffs(
  meetingSummary: Record<string, unknown> | null | undefined
): VosDisputeFieldDiffs {
  const ms = meetingSummary ?? {}

  const clientStart = ms.reqStartDate ?? ms.ReqStartDate
  const clientEnd = ms.reqEndDate ?? ms.ReqEndDate
  const terpStart = ms.startDate ?? ms.StartDate
  const terpEnd = ms.endDate ?? ms.EndDate

  const clientMileage = ms.requesterMilage ?? ms.RequesterMilage
  const terpMileage = ms.milage ?? ms.Milage

  const clientRoundTrip = ms.requesterIsRoundTrip ?? ms.RequesterIsRoundTrip
  const terpRoundTrip = ms.isRoundTrip ?? ms.IsRoundTrip

  const clientTravelH = Number(ms.requesterTravelTimeH ?? ms.RequesterTravelTimeH ?? 0)
  const clientTravelM = Number(ms.requesterTravelTimeM ?? ms.RequesterTravelTimeM ?? 0)
  const terpTravelH = Number(ms.travalTimeH ?? ms.TravalTimeH ?? 0)
  const terpTravelM = Number(ms.travalTimeM ?? ms.TravalTimeM ?? 0)

  const clientBreakTime = ms.requesterBreakTimeMinutes ?? ms.RequesterBreakTimeMinutes
  const terpBreakTime = ms.breakTimeMinutes ?? ms.BreakTimeMinutes

  const startTimeDiff = !sameInstant(clientStart, terpStart)
  const endTimeDiff = !sameInstant(clientEnd, terpEnd)
  const breakTimeDiff = !sameNumber(clientBreakTime, terpBreakTime)

  return {
    startTime: startTimeDiff,
    endTime: endTimeDiff,
    duration: startTimeDiff || endTimeDiff,
    breakTime: breakTimeDiff,
    mileage: !sameNumber(clientMileage, terpMileage),
    roundTrip: Boolean(clientRoundTrip) !== Boolean(terpRoundTrip),
    travelTime: clientTravelH !== terpTravelH || clientTravelM !== terpTravelM,
  }
}

export function vosDisputeRowClassName(
  highlight: boolean,
  fieldDiff: boolean
): string {
  return highlight && fieldDiff ? VOS_DISPUTE_ROW_HIGHLIGHT_CLASS : VOS_DISPUTE_ROW_CLASS
}
